import { getPluginService } from "./services/plugin/plugin-service";
import { PluginSettingsStorage } from "./services/plugin/plugin-settings-storage";
import { type ImportResult } from "@saludio/core";

type BackupData = {
  version: number;
  exportDate: string;
  plugins: Record<string, any>;
};

/**
 * Export data from all plugins
 */
const exportDatabaseToJSON = async (): Promise<string> => {
  const pluginData: Record<string, any> = {};
  const pluginIds = getPluginService().getAllPluginIds();

  for (const pluginId of pluginIds) {
    const module = getPluginService().getPluginEntry(pluginId);
    if (module?.registration) {
      try {
        const data = await module.registration.exportData();
        pluginData[pluginId] = data;
        console.log(`Exported data for plugin: ${pluginId}`, data);
      } catch (error) {
        console.error(`Failed to export data for plugin ${pluginId}:`, error);
      }
    }
  }

  const backup: BackupData = {
    version: 1,
    exportDate: new Date().toISOString(),
    plugins: pluginData,
  };

  return JSON.stringify(backup, null, 2);
};

/**
 * Import data to all plugins from backup
 */
const importDatabaseFromJSON = async (
  content: string,
): Promise<ImportResult> => {
  const result: ImportResult = {
    imported: 0,
    skipped: 0,
    errors: 0,
  };

  try {
    const backup: BackupData = JSON.parse(content);

    if (!backup.plugins || typeof backup.plugins !== "object") {
      throw new Error("Invalid backup format: missing plugins data");
    }

    const pluginIds = getPluginService().getAllPluginIds();

    for (const pluginId of pluginIds) {
      const module = getPluginService().getPluginEntry(pluginId);
      const pluginBackupData = backup.plugins[pluginId];

      if (module?.registration && pluginBackupData) {
        try {
          const pluginResult = await module.registration.importData(pluginBackupData);
          result.imported += pluginResult.imported;
          result.skipped += pluginResult.skipped;
          result.errors += pluginResult.errors;
          console.log(`Imported data for plugin ${pluginId}:`, pluginResult);
        } catch (error) {
          console.error(`Failed to import data for plugin ${pluginId}:`, error);
          result.errors++;
        }
      }
    }

    return result;
  } catch (error) {
    console.error("Failed to parse backup:", error);
    throw error;
  }
};

/**
 * Clear all data
 */
const clearDatabase = async (): Promise<void> => {
  const pluginIds = getPluginService().getAllPluginIds();

  for (const pluginId of pluginIds) {
    const module = getPluginService().getPluginEntry(pluginId);
    if (module?.registration) {
      try {
        await module.registration.clearData();
        console.log(`Cleared data for plugin: ${pluginId}`);
      } catch (error) {
        console.error(`Failed to clear data for plugin ${pluginId}:`, error);
        throw error;
      }
    }
  }

  PluginSettingsStorage.clear();
};

export const downloadBackup = async (): Promise<void> => {
  const jsonContent = await exportDatabaseToJSON();
  const blob = new Blob([jsonContent], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `saludio-database-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const restoreBackup = async (): Promise<ImportResult> => {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      try {
        const content = await file.text();
        const result = await importDatabaseFromJSON(content);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    input.oncancel = () => {
      reject(new Error("File selection cancelled"));
    };

    input.click();
  });
};

export const factoryReset = async (): Promise<void> => {
  const confirmed = confirm(
    "WARNING: Factory Reset\n\n" +
    "This will permanently delete ALL data from the database.\n" +
    "This action cannot be undone.\n\n" +
    "Are you sure you want to continue?",
  );

  if (!confirmed) {
    throw new Error("Factory reset cancelled");
  }

  await clearDatabase();
};
